import {
  resolveVetcanApiBaseUrl,
  getVetcanApiBaseUrl,
  vetcanUrl,
} from "./apiBase";
import { buildTenantHeaders } from "./runtimeTenant";

export type VetcanToolResult = {
  ok: boolean;
  action: string;
  correlationId: string;
  entityId?: string;
  message: string;
};

export type VetcanChatResponse = {
  conversationId: string;
  correlationId: string;
  reply: string;
  escalate: boolean;
  category: string;
  topic?: string;
  toolResults?: VetcanToolResult[];
};

export type VetcanChatRequest = {
  conversationId: string;
  correlationId?: string;
  message: string;
  toolRequests?: Array<{ action: string; payload?: Record<string, unknown> }>;
};

export type VetcanAssistantRole = "client_viewer";
export type VetcanAssistantConfidence = "grounded" | "limited" | "not_applicable";

export type VetcanAssistantCitation = {
  label: string;
  source: string;
  sectionLabel?: string;
  lineStart?: number;
  lineEnd?: number;
  chunkId?: string;
  excerpt?: string;
};

export type VetcanAssistantEvidence = {
  label: string;
  source: string;
  sectionLabel?: string;
  lineStart?: number;
  lineEnd?: number;
  excerpt: string;
};

export type VetcanAssistantConfidenceNote = {
  title: string;
  detail: string;
};

export type VetcanAssistantSourceSummary = {
  label: string;
  detail: string;
};

export type VetcanAssistantArtifactStatus = {
  label: string;
  source: string;
  present: boolean;
};

export type VetcanAssistantResponse = {
  status: "answered" | "blocked";
  role: VetcanAssistantRole;
  topic:
    | "system_posture"
    | "approval_workflow"
    | "preview_voice"
    | "scheduling_truth"
    | "payment_boundaries"
    | "offer_truth"
    | "artifact_truth"
    | "compliance_boundaries"
    | "unsupported";
  answer: string;
  confidence: VetcanAssistantConfidence;
  readOnly: true;
  sourceScope: "checked_in_truth_plus_workspace_artifact_checks";
  boundaries: string[];
  confidenceNote: VetcanAssistantConfidenceNote;
  sourceSummaries: VetcanAssistantSourceSummary[];
  citations: VetcanAssistantCitation[];
  evidence: VetcanAssistantEvidence[];
  artifactTruth: {
    build: VetcanAssistantArtifactStatus;
    demo: VetcanAssistantArtifactStatus;
    offer: VetcanAssistantArtifactStatus;
    navigation: VetcanAssistantArtifactStatus;
  };
};

export type VetcanAssistantRequest = {
  question: string;
  role?: VetcanAssistantRole;
};

export type ClinicProfileResponse = {
  tenantKey: string;
  displayName: string;
  addressText: string;
  hoursText: string;
  parkingText: string;
  renewalsPhone: string;
  supportPhone: string;
  noMedicalAdviceNotice: string;
};

const SAFE_FETCH_ERROR_MESSAGE =
  "We’re having trouble reaching the clinic system right now. Please try again.";
const DEFAULT_TIMEOUT_MS = 10_000;
const CONNECTIVITY_HINT =
  "Check VITE_VETCAN_API_BASE_URL or Vite /api proxy.";

export { resolveVetcanApiBaseUrl, getVetcanApiBaseUrl, vetcanUrl };

export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs = DEFAULT_TIMEOUT_MS
) {
  const controller = new AbortController();
  const externalSignal = init.signal;
  const onAbort = () => controller.abort();

  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener("abort", onAbort, { once: true });
    }
  }

  const timeoutId = globalThis.setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
    if (externalSignal) {
      externalSignal.removeEventListener("abort", onAbort);
    }
  }
}

export async function postVetcanChat(
  payload: VetcanChatRequest,
  tenantKey?: string
): Promise<VetcanChatResponse> {
  try {
    const res = await fetchWithTimeout(vetcanUrl("/api/vetcan/chat"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...buildTenantHeaders(tenantKey),
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error(`Chat backend unreachable (HTTP ${res.status}). ${CONNECTIVITY_HINT}`);
    }

    const parsed = (await res.json()) as VetcanChatResponse;
    if (
      typeof parsed?.reply !== "string" ||
      typeof parsed?.conversationId !== "string" ||
      typeof parsed?.correlationId !== "string"
    ) {
      throw new Error(`Chat backend returned an invalid payload. ${CONNECTIVITY_HINT}`);
    }

    return parsed;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Chat backend unreachable. ${CONNECTIVITY_HINT}`);
  }
}

export async function postVetcanAssistant(
  payload: VetcanAssistantRequest,
  tenantKey?: string
): Promise<VetcanAssistantResponse> {
  const res = await fetchWithTimeout(vetcanUrl("/api/vetcan/assistant"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildTenantHeaders(tenantKey),
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error(
      `Knowledge assistant unavailable (HTTP ${res.status}). ${CONNECTIVITY_HINT}`
    );
  }

  const parsed = (await res.json()) as VetcanAssistantResponse;
  if (
    typeof parsed?.answer !== "string" ||
    !Array.isArray(parsed?.citations) ||
    !Array.isArray(parsed?.boundaries) ||
    !Array.isArray(parsed?.evidence) ||
    !Array.isArray(parsed?.sourceSummaries) ||
    typeof parsed?.confidenceNote?.title !== "string" ||
    typeof parsed?.confidenceNote?.detail !== "string" ||
    typeof parsed?.confidence !== "string"
  ) {
    throw new Error(
      `Knowledge assistant returned an invalid payload. ${CONNECTIVITY_HINT}`
    );
  }

  return parsed;
}

export async function getApiHealth() {
  try {
    const res = await fetchWithTimeout(vetcanUrl("/api/health"));
    if (!res.ok) throw new Error(SAFE_FETCH_ERROR_MESSAGE);
    return (await res.json()) as {
      ok: boolean;
      service: string;
      basePath: string;
    };
  } catch {
    throw new Error(SAFE_FETCH_ERROR_MESSAGE);
  }
}

function isClinicProfilePayload(
  value: unknown
): value is ClinicProfileResponse {
  const record = value as Record<string, unknown> | null;
  if (!record) return false;
  return (
    typeof record.tenantKey === "string" &&
    typeof record.displayName === "string" &&
    typeof record.addressText === "string" &&
    typeof record.hoursText === "string" &&
    typeof record.parkingText === "string" &&
    typeof record.renewalsPhone === "string" &&
    typeof record.supportPhone === "string" &&
    typeof record.noMedicalAdviceNotice === "string"
  );
}

export async function getClinicProfile(tenantKey?: string) {
  const res = await fetchWithTimeout(vetcanUrl("/api/clinic-profile"), {
    headers: {
      ...buildTenantHeaders(tenantKey),
    },
  });
  if (!res.ok) {
    throw new Error(`Clinic profile unavailable (HTTP ${res.status})`);
  }

  const payload = (await res.json()) as unknown;
  if (!isClinicProfilePayload(payload)) {
    throw new Error("Clinic profile payload was invalid");
  }

  return payload;
}
